import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

// Definir límites de plan
const PLAN_LIMITS = {
  2: { // vendedor
    'gratuito': 1,
    'casual': 5
  },
  4: { // inmobiliaria
    'basico': 10,
    'estandar': 20,
    'premium': Infinity
  },
  3: { // constructora - todos los planes posibles
    'gratuito': Infinity,
    'casual': Infinity,
    'basico': Infinity,
    'estandar': Infinity,
    'premium': Infinity
  }
};

export const usePlanValidation = () => {
  const { user } = useAuth();
  const [canPublish, setCanPublish] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [planInfo, setPlanInfo] = useState({
    typeId: null,
    plan: null,
    totalProperties: 0,
    limit: 0,
    remaining: 0
  });

  useEffect(() => {
    const validatePublishingPermission = async () => {
      setLoading(true);
      setError(null);
    
      try {
        // Verificar si hay usuario autenticado
        if (!user) {
          console.log('No hay usuario autenticado');
          setError('Usuario no autenticado');
          setCanPublish(false);
          setLoading(false);
          return;
        }
    
        // Obtener y convertir datos del usuario
        const userTypeId = parseInt(user.tipo_usuario_id) || 2;
        let currentPlan = String(user.plan || '').toLowerCase().trim() || 'gratuito';
    
        // 🔥 NUEVA VERIFICACIÓN: Estado de mora y suspensión
        let estamoroso = false;
        let estaSuspendido = false;
        let diasVencido = 0;
        
        try {
          const token = localStorage.getItem('token');
          
          const morosoResponse = await fetch('https://backend-production-1786.up.railway.app/api/vendedores/estado-moroso', {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
    
          if (morosoResponse.ok) {
            const morosoData = await morosoResponse.json();
            estamoroso = morosoData.esMoroso || false;
            estaSuspendido = morosoData.estaSuspendido || false;
            diasVencido = morosoData.diasVencido || 0;
            
            console.log('Estado de mora verificado:', {
              estamoroso,
              estaSuspendido,
              diasVencido
            });
          } else {
            console.warn('No se pudo verificar estado de mora, continuando con validación normal');
          }
        } catch (morosoError) {
          console.warn('Error verificando estado de mora:', morosoError);
          // Continuar con validación normal si falla la verificación
        }
    
        // 🚨 BLOQUEAR PUBLICACIÓN SI ESTÁ SUSPENDIDO
        if (estaSuspendido) {
          const mensajeSuspension = diasVencido > 0 
            ? `Tu cuenta está suspendida por mora de ${diasVencido} días. Regulariza tu pago para reactivar tus publicaciones.`
            : 'Tu cuenta está suspendida. Contacta a soporte para más información.';
          
          setError(mensajeSuspension);
          setCanPublish(false);
          setPlanInfo({
            typeId: userTypeId,
            plan: currentPlan,
            totalProperties: parseInt(user.total_propiedades) || 0,
            limit: 0,
            remaining: 0,
            suspendido: true,
            diasVencido
          });
          setLoading(false);
          return;
        }
    
        // ⚠️ MOSTRAR ADVERTENCIA SI ESTÁ MOROSO PERO NO SUSPENDIDO
        if (estamoroso && diasVencido > 0 && diasVencido < 7) {
          console.log(`⚠️ Usuario moroso: ${diasVencido} días vencido`);
          // Continuar con validación normal pero mostrar advertencia
        }
    
        // Verificar que el plan existe para este tipo de usuario
        const planConfig = PLAN_LIMITS[userTypeId];
        const planLimit = planConfig ? planConfig[currentPlan] : undefined;
    
        console.log('Validación de plan para:', {
          userTypeId,
          currentPlan,
          planConfig: JSON.stringify(planConfig),
          planLimit,
          estamoroso,
          diasVencido,
          allLimits: JSON.stringify(PLAN_LIMITS)
        });
    
        // Intentar obtener total de propiedades del API
        let totalProperties = 0;
        let apiPlan = currentPlan;
        let apiSuccess = false;
        
        try {
          const token = localStorage.getItem('token');
          
          const response = await fetch('https://backend-production-1786.up.railway.app/api/vendedores/plan-info', {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
    
          if (response.ok) {
            const data = await response.json();
            console.log('API data recibida:', data);
            
            // IMPORTANTE: Actualizar los datos
            totalProperties = parseInt(data.total_propiedades) || 0;
            
            // Actualizar el plan si viene de la API y es válido
            if (data.plan) {
              apiPlan = String(data.plan).toLowerCase().trim();
              console.log('Plan obtenido de API:', apiPlan);
            }
            
            apiSuccess = true;
          } else {
            // Fallback a localStorage
            console.warn('Error en API:', response.status, response.statusText);
            totalProperties = parseInt(user.total_propiedades) || 0;
          }
        } catch (apiError) {
          console.error('Error de conexión API:', apiError);
          // Fallback a localStorage
          totalProperties = parseInt(user.total_propiedades) || 0;
        }
    
        // IMPORTANTE: Usar el plan de la API si fue exitosa la llamada
        if (apiSuccess) {
          currentPlan = apiPlan;
        }
        
        // Re-verificar el límite del plan después de posible actualización desde API
        const updatedPlanConfig = PLAN_LIMITS[userTypeId];
        let updatedPlanLimit = updatedPlanConfig ? updatedPlanConfig[currentPlan] : undefined;
        
        // Si no encontramos el plan en la configuración, manejamos caso especial
        if (!updatedPlanConfig || updatedPlanLimit === undefined) {
          console.warn(`Plan no configurado: tipo=${userTypeId}, plan=${currentPlan}`);
          
          // Fallback a plan gratuito para tipo 2
          let fallbackPlan = 'gratuito';
          let fallbackLimit = 1;
          
          // Lógica específica por tipo de usuario
          if (userTypeId === 2) {
            // CORRECCIÓN IMPORTANTE: Si el plan es "casual", vamos a asignar ese valor aunque no esté en configuración
            if (currentPlan === 'casual') {
              fallbackPlan = 'casual';
              fallbackLimit = 5; // Límite para plan casual
            } else {
              fallbackPlan = 'gratuito';
              fallbackLimit = 1;
            }
          } else if (userTypeId === 3) {
            // Las constructoras tienen publicaciones ilimitadas
            fallbackPlan = currentPlan; // Mantenemos el plan actual
            fallbackLimit = Infinity;
          } else if (userTypeId === 4) {
            fallbackPlan = 'basico';
            fallbackLimit = 10;
          }
          
          const newPlanInfo = {
            typeId: userTypeId,
            plan: fallbackPlan,
            totalProperties,
            limit: fallbackLimit,
            remaining: Math.max(0, fallbackLimit - totalProperties),
            // 🔥 NUEVOS CAMPOS DE MORA
            estamoroso,
            diasVencido,
            suspendido: false
          };
          
          setPlanInfo(newPlanInfo);
          
          // Para tipo 2, permitir publicar si total < limite
          const canUserPublish = totalProperties < fallbackLimit;
          setCanPublish(canUserPublish);
          
          if (!canUserPublish) {
            setError(getUserMessage(userTypeId, fallbackPlan, totalProperties, fallbackLimit, estamoroso, diasVencido));
          } else if (estamoroso && diasVencido > 0) {
            // Mostrar advertencia de mora sin bloquear
            setError(getMensajeMora(diasVencido));
          }
          
          setLoading(false);
          return;
        }
    
        // Actualizar información del plan
        const limit = updatedPlanLimit;
        const newPlanInfo = {
          typeId: userTypeId,
          plan: currentPlan,
          totalProperties,
          limit,
          remaining: Math.max(0, limit - totalProperties),
          // 🔥 NUEVOS CAMPOS DE MORA
          estamoroso,
          diasVencido,
          suspendido: false
        };
    
        setPlanInfo(newPlanInfo);
    
        // Determinar si puede publicar según tipo de usuario
        let publishAllowed = false;
    
        switch (userTypeId) {
          case 3: // Constructora
            console.log('Constructora detectada - siempre puede publicar');
            publishAllowed = true; // Las constructoras siempre pueden publicar
            break;
          case 2: // Vendedor
            console.log('Vendedor detectado:', currentPlan, 'total:', totalProperties, 'límite:', limit);
            // CORRECCIÓN: Comprobar claramente si el plan es casual o gratuito
            if (currentPlan === 'casual') {
              publishAllowed = totalProperties < 5; // Explícitamente usar el límite del plan casual
              console.log('Decisión para plan casual:', publishAllowed, 'total:', totalProperties);
            } else if (currentPlan === 'gratuito') {
              publishAllowed = totalProperties < 1;
              console.log('Decisión para plan gratuito:', publishAllowed);
            } else {
              publishAllowed = totalProperties < limit;
            }
            break;
          case 4: // Inmobiliaria
            publishAllowed = totalProperties < limit;
            break;
          default:
            publishAllowed = false;
        }
    
        setCanPublish(publishAllowed);
    
        // Establecer mensaje de error si no puede publicar
        if (!publishAllowed) {
          setError(getUserMessage(userTypeId, currentPlan, totalProperties, limit, estamoroso, diasVencido));
        } else if (estamoroso && diasVencido > 0) {
          // Mostrar advertencia de mora sin bloquear publicación
          setError(getMensajeMora(diasVencido));
        }
        
      } catch (error) {
        console.error('Error general en validación:', error);
        setError('Error al validar permisos de publicación');
        setCanPublish(false);
      } finally {
        setLoading(false);
      }
    };
    
    // 🔥 NUEVA FUNCIÓN: Mensaje específico para mora
    const getMensajeMora = (diasVencido) => {
      if (diasVencido <= 3) {
        return `⚠️ Tu plan venció hace ${diasVencido} día${diasVencido > 1 ? 's' : ''}. Regulariza tu pago pronto para evitar suspensión.`;
      } else if (diasVencido <= 6) {
        return `🚨 ¡ATENCIÓN! Tu plan lleva ${diasVencido} días vencido. Tienes ${7 - diasVencido} días antes de que se suspendan tus publicaciones.`;
      } else {
        return `🔥 ¡ÚLTIMO DÍA! Tu plan lleva ${diasVencido} días vencido. Paga HOY para evitar suspensión.`;
      }
    };
    

    if (user) {
      validatePublishingPermission();
    } else {
      setLoading(false);
      setCanPublish(false);
    }
  }, [user]);

  const getUserMessage = (typeId, plan, total, limit, estamoroso = false, diasVencido = 0) => {
    const planNames = {
      gratuito: 'Gratuito',
      casual: 'Casual',
      basico: 'Básico',
      estandar: 'Estándar',
      premium: 'Premium'
    };
  
    const planName = planNames[plan] || plan;
    
    let baseMessage = '';
    
    if (limit === Infinity) {
      baseMessage = `Tu plan ${planName} te permite publicaciones ilimitadas.`;
    } else {
      baseMessage = `Has alcanzado el límite de tu plan ${planName}. Tienes ${total} de ${limit} publicaciones permitidas.`;
    }
    
    // 🔥 AGREGAR INFORMACIÓN DE MORA SI APLICA
    if (estamoroso && diasVencido > 0) {
      if (diasVencido < 7) {
        baseMessage += ` Además, tu plan venció hace ${diasVencido} día${diasVencido > 1 ? 's' : ''}. Regulariza tu pago para evitar suspensión.`;
      } else {
        baseMessage += ` ⚠️ Tu cuenta está en riesgo de suspensión por ${diasVencido} días de mora.`;
      }
    }
    
    return baseMessage;
  };

  return {
    canPublish,
    loading,
    error,
    planInfo
  };
};

export default usePlanValidation;